import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import type {AccessProfileStatus} from './access-profile'
import type {SetAttributeRequest} from './attribute-definition'
import type {AssignmentEventType, AssignmentSourceType} from './assignment'
import type {EscalationLevel} from './escalation'
import type {InitialOwnerRequest, OwnerResponse} from './owner'
import type {EntityAttribute} from './resource'
import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
import {CheckCircle, Clock, XCircle, type LucideIcon} from 'lucide-react'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

export interface FunctionGroup {
    id: UUID
    name: string
    functionGroupType: string
    description: string | null
    parent: EntityLink | null
    autoAssignable: boolean
    requestable: boolean
    expiryRequired: boolean
    maxDurationDays: number | null
    version: number
    createdAt: Instant
    updatedAt: Instant
    attributes: EntityAttribute[] | null
    owners: OwnerResponse[] | null
}

export interface FunctionGroupTree {
    id: UUID
    name: string
    functionGroupType: string
    description: string | null
    autoAssignable: boolean
    requestable: boolean
    expiryRequired: boolean
    maxDurationDays: number | null
    version: number
    children: FunctionGroupTree[]
}

// ======== Function Group Assignments ========

export const FunctionGroupAssignmentStatus = {
    ACTIVE: 'ACTIVE',
    PENDING: 'PENDING',
    EXPIRED: 'EXPIRED',
    REVOKED: 'REVOKED',
} as const
export type FunctionGroupAssignmentStatus = typeof FunctionGroupAssignmentStatus[keyof typeof FunctionGroupAssignmentStatus]

/**
 * Rich enum carrying UI rendering metadata (badge variant + icon) for
 * {@link FunctionGroupAssignmentStatus} wire values. Labels are resolved
 * via the i18n-backed {@code useFunctionGroupAssignmentStatusLabel} hook
 * since translations live in the existing `functionGroupAssignments`
 * namespace — keeping them here would create a second source of truth.
 */
export class FunctionGroupAssignmentStatusBadge extends EnumValue {
    constructor(
        code: string,
        variant: BadgeVariant,
        public readonly icon: LucideIcon,
    ) {
        super(code, variant)
    }

    static readonly ACTIVE = new FunctionGroupAssignmentStatusBadge(
        FunctionGroupAssignmentStatus.ACTIVE, BadgeVariant.SUCCESS, CheckCircle,
    )
    static readonly PENDING = new FunctionGroupAssignmentStatusBadge(
        FunctionGroupAssignmentStatus.PENDING, BadgeVariant.SECONDARY, Clock,
    )
    static readonly EXPIRED = new FunctionGroupAssignmentStatusBadge(
        FunctionGroupAssignmentStatus.EXPIRED, BadgeVariant.OUTLINE, Clock,
    )
    static readonly REVOKED = new FunctionGroupAssignmentStatusBadge(
        FunctionGroupAssignmentStatus.REVOKED, BadgeVariant.DESTRUCTIVE, XCircle,
    )

    static readonly values: ReadonlyArray<FunctionGroupAssignmentStatusBadge> = [
        FunctionGroupAssignmentStatusBadge.ACTIVE,
        FunctionGroupAssignmentStatusBadge.PENDING,
        FunctionGroupAssignmentStatusBadge.EXPIRED,
        FunctionGroupAssignmentStatusBadge.REVOKED,
    ]

    static of(code: FunctionGroupAssignmentStatus | null | undefined): FunctionGroupAssignmentStatusBadge | undefined {
        return FunctionGroupAssignmentStatusBadge.values.find((v) => v.code === code)
    }
}

export interface FunctionGroupAssignmentSource {
    id: UUID
    sourceType: AssignmentSourceType
    ruleId: UUID | null
    requestItemId: UUID | null
    assignedAt: Instant
    removedAt: Instant | null
}

export interface FunctionGroupAssignmentEvent {
    id: UUID
    eventType: AssignmentEventType
    oldExpiresAt: Instant | null
    newExpiresAt: Instant | null
    performedBy: EntityLink | null
    comment: string | null
    createdAt: Instant
}

export interface FunctionGroupAssignment {
    id: UUID
    identity: EntityLink
    functionGroupId: UUID
    functionGroupName: string
    status: FunctionGroupAssignmentStatus
    startAt: Instant
    expiresAt: Instant
    revokedAt: Instant | null
    permanent: boolean
    version: number
    sources: FunctionGroupAssignmentSource[]
    events: FunctionGroupAssignmentEvent[]
}

// ======== Function Group Requests ========

import type {RequestItemStatus, RequestStatus} from './request-base'
export {RequestItemStatus, RequestStatus} from './request-base'

export interface FunctionGroupRequestItemApprover {
    id: UUID
    approver: EntityLink
    assignedAt: Instant
}

export interface FunctionGroupRequestApproval {
    id: UUID
    approver: EntityLink
    decision: string
    comment: string | null
    decidedAt: Instant
}

export interface FunctionGroupRequestApprovalStep {
    id: UUID
    stepOrder: number
    resolverType: string
    resolverParam: string | null
    approvalStrategy: string
    required: boolean
    status: string
    activatedAt: Instant | null
    completedAt: Instant | null
    escalationLevel: EscalationLevel
    lastEscalationAt: Instant | null
    approvers: FunctionGroupRequestItemApprover[]
    decisions: FunctionGroupRequestApproval[]
}

export interface FunctionGroupRequestItem {
    id: UUID
    requestId: UUID
    functionGroup: EntityLink
    status: RequestItemStatus
    approvalStrategy: string
    matchedPolicyName: string
    validFrom: Instant | null
    expiresAt: Instant | null
    approvedAt: Instant | null
    fulfilledAt: Instant | null
    rejectedAt: Instant | null
    cancelledAt: Instant | null
    cancelReason: string | null
    requester: EntityLink
    beneficiary: EntityLink
    justification: string
    approvalChain: FunctionGroupRequestApprovalStep[]
    assignedApprovers: FunctionGroupRequestItemApprover[]
    approvals: FunctionGroupRequestApproval[]
    version: number
    createdAt: Instant
}

export interface FunctionGroupRequest {
    id: UUID
    requester: EntityLink
    beneficiary: EntityLink
    justification: string
    status: RequestStatus
    items: FunctionGroupRequestItem[]
    version: number
    createdAt: Instant
    updatedAt: Instant
}

export interface FunctionGroupRequestCreateRequest {
    beneficiaryIdentityId: UUID
    justification: string
    items: FunctionGroupRequestItemCreate[]
}

export interface FunctionGroupRequestItemCreate {
    functionGroupId: UUID
    expiresAt?: Instant
}

export interface FunctionGroupRequestPage {
    content: FunctionGroupRequest[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface FunctionGroupRequestItemPage {
    content: FunctionGroupRequestItem[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface FunctionGroupBatchApproveRequest {
    itemIds: UUID[]
    comment?: string
}

export interface FunctionGroupBatchRejectRequest {
    itemIds: UUID[]
    comment: string
}

export interface FunctionGroupCreateRequest {
    functionGroupType: string
    description?: string | null
    parentId?: UUID | null
    owners: InitialOwnerRequest[]
    autoAssignable: boolean
    requestable: boolean
    expiryRequired: boolean
    maxDurationDays?: number | null
    attributes?: SetAttributeRequest[]
}

export interface FunctionGroupUpdateRequest {
    name: string
    functionGroupType: string
    description?: string
    parentId?: UUID
    autoAssignable: boolean
    requestable: boolean
    expiryRequired: boolean
    maxDurationDays?: number
    version: number
}

export interface FunctionGroupAttributeEntry {
    definitionId: UUID
    valueType: string
    value?: unknown
}

export interface FunctionGroupAttributeSetRequest {
    attributes: FunctionGroupAttributeEntry[]
}

// ======== Function Group Access Profiles ========

export interface AccessProfileFunctionGroup {
    id: UUID
    functionGroup: EntityLink
}

export interface FunctionGroupAccessProfile {
    id: UUID
    accessProfile: EntityLink
    resource: EntityLink | null
    environment: string
    riskLevel: string
    status: AccessProfileStatus
}

export interface FunctionGroupAccessProfileList {
    items: FunctionGroupAccessProfile[]
    truncated: boolean
    totalCount: number
}

export interface AddFunctionGroupAccessProfilesRequest {
    accessProfileIds: UUID[]
}

// ======== Inherited Access Profile Assignments ========

export interface InheritedAccessProfileAssignment {
    accessProfile: EntityLink
    resource: EntityLink | null
    environment: string
    riskLevel: string
    origin: string
    functionGroup: EntityLink
    startAt: Instant
    expiresAt: Instant
    status: AccessProfileStatus
}


/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const FunctionGroupAssignmentResponseFields = {
    id: 'id',
    identity: 'identity',
    functionGroupId: 'functionGroupId',
    functionGroupName: 'functionGroupName',
    status: 'status',
    startAt: 'startAt',
    expiresAt: 'expiresAt',
    revokedAt: 'revokedAt',
    permanent: 'permanent',
    version: 'version',
    sources: 'sources',
    events: 'events',
} as const

export const FunctionGroupCreateRequestFields = {
    functionGroupType: 'functionGroupType',
    description: 'description',
    parentId: 'parentId',
    owners: 'owners',
    autoAssignable: 'autoAssignable',
    requestable: 'requestable',
    expiryRequired: 'expiryRequired',
    maxDurationDays: 'maxDurationDays',
    attributes: 'attributes',
} as const

export const InitialOwnerRequestFields = {
    ownerPartyId: 'ownerPartyId',
    responsibility: 'responsibility',
} as const

export const FunctionGroupResponseFields = {
    id: 'id',
    name: 'name',
    functionGroupType: 'functionGroupType',
    description: 'description',
    parent: 'parent',
    autoAssignable: 'autoAssignable',
    requestable: 'requestable',
    expiryRequired: 'expiryRequired',
    maxDurationDays: 'maxDurationDays',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
    attributes: 'attributes',
    owners: 'owners',
} as const

export const FunctionGroupTreeResponseFields = {
    id: 'id',
    name: 'name',
    functionGroupType: 'functionGroupType',
    description: 'description',
    autoAssignable: 'autoAssignable',
    requestable: 'requestable',
    expiryRequired: 'expiryRequired',
    maxDurationDays: 'maxDurationDays',
    version: 'version',
    children: 'children',
} as const

export const FunctionGroupUpdateRequestFields = {
    name: 'name',
    functionGroupType: 'functionGroupType',
    description: 'description',
    parentId: 'parentId',
    autoAssignable: 'autoAssignable',
    requestable: 'requestable',
    expiryRequired: 'expiryRequired',
    maxDurationDays: 'maxDurationDays',
    version: 'version',
} as const

export const FunctionGroupRequestCreateRequestFields = {
    beneficiaryIdentityId: 'beneficiaryIdentityId',
    justification: 'justification',
    items: 'items',
} as const

// CPD-OFF — parallel-by-domain with AccessRequestResponseFields in access-request.ts. Each mirrors a distinct backend governance-request Record (FunctionGroupRequestResponse vs AccessRequestResponse) that happens to share field names; collapsing them would lose the response-record-to-fields-constant invariant enforced by RequireResponseTypescriptMatch.
export const FunctionGroupRequestResponseFields = {
    id: 'id',
    requester: 'requester',
    beneficiary: 'beneficiary',
    justification: 'justification',
    status: 'status',
    items: 'items',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const FunctionGroupRequestFilterParams = {
    status: 'status',
    page: 'page',
} as const
/* eslint-enable i18next/no-literal-string */
